import React from "react";

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input as ShadcnInput } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS,
  CUSTOM_CSV_FIELD_CARD_CLASS,
  CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS,
  CUSTOM_CSV_SELECT_CONTENT_PROPS,
} from "./customCsvWizardUi";
import type { CustomCsvImportCapabilities } from "../../../services/brokerIntegrationApi";
import type { CustomCsvWizardState } from "../../../types/customCsvImport";
import {
  applyColumnMappingChange,
  getMappableFieldsForMode,
  orderMappableFieldsForShape,
  preferredPnlFieldForMode,
  resolveCsvHeadersForMapping,
} from "../../../utils/customCsvImport";
import { findLayoutShape } from "../../../utils/layoutShapes";
import ColumnMappingDiagram from "./visuals/ColumnMappingDiagram";

export interface CustomCsvColumnMappingStepProps {
  csvImport: CustomCsvImportCapabilities;
  state: CustomCsvWizardState;
  fileHeaders: string[];
  onStateChange: (patch: Partial<CustomCsvWizardState>) => void;
}

const BROKER_SELECT_TRIGGER_CLASS = CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS;

const COLUMN_MAPPING_FIELD_CARD_CONTENT_CLASS = CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS;

const COLUMN_MAPPING_SELECT_CONTENT_PROPS = CUSTOM_CSV_SELECT_CONTENT_PROPS;

const CustomCsvColumnMappingStep = ({
  csvImport,
  state,
  fileHeaders,
  onStateChange,
}: CustomCsvColumnMappingStepProps): React.ReactElement => {
  const layoutShape = findLayoutShape(csvImport, state.layoutShapeId);
  const mappableFields = orderMappableFieldsForShape(
    getMappableFieldsForMode(csvImport, state.importMode),
    layoutShape
  );
  const headers = resolveCsvHeadersForMapping(fileHeaders, state.preview?.headers);
  const highlightFields = new Set(layoutShape?.highlight_fields ?? []);
  const preferredPnlField = preferredPnlFieldForMode(state.importMode);
  const hasBrokerPnlMapped = Boolean(state.columnMapping[preferredPnlField]?.trim());
  const hasConversionRateMapped = Boolean(state.columnMapping.conversion_rate?.trim());
  const preferredPnlLabel = preferredPnlField.replace(/_/g, " ");
  const hasHeldMapped = Boolean(state.columnMapping.held_duration?.trim());
  const mappingRequiredFields = new Set(
    (layoutShape?.required_fields ?? []).filter(
      (field) => !(hasHeldMapped && field === "exit_time")
    )
  );

  const setMapping = (field: string, header: string) => {
    onStateChange({
      columnMapping: applyColumnMappingChange(state.columnMapping, field, header),
      preview: null,
      acknowledgedWarningCodes: new Set(),
    });
  };

  return (
    <div className="flex flex-col gap-5" data-custom-csv-column-mapping="">
      <Alert variant="info" className={BROKER_ALERT_CLASS}>
        <BrokerAlertIcon tone="info" />
        <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>Map P&L first when available</AlertTitle>
        <AlertDescription className={cn(BROKER_ALERT_DESC_CLASS, "space-y-2")}>
          <p className="m-0">
            If your CSV includes broker-reported P&L, map{" "}
            <span className={BROKER_ALERT_TITLE_CLASS}>{preferredPnlLabel}</span> first. That is
            always preferred.
          </p>
          <p className="m-0">
            When P&L is missing on FX crosses, map{" "}
            <span className={BROKER_ALERT_TITLE_CLASS}>conversion_rate</span> (e.g. OANDA BALANCE on
            the close row). For CFDs/futures without P&L, set contract multipliers on the Account
            details step.
          </p>
          {hasBrokerPnlMapped ? (
            <p className="m-0">
              Broker P&L column mapped. conversion_rate is only needed as a fallback.
            </p>
          ) : hasConversionRateMapped ? (
            <p className="m-0">
              conversion_rate mapped. Set statement account currency on Account details if preview
              warns about FX P&L.
            </p>
          ) : null}
        </AlertDescription>
      </Alert>

      <ColumnMappingDiagram
        fileHeaders={headers}
        mappableFields={mappableFields}
        columnMapping={state.columnMapping}
        highlightFields={highlightFields}
        requiredFields={mappingRequiredFields}
        onMappingChange={setMapping}
      />

      {!state.columnMapping.symbol?.trim() ? (
        <Card
          data-custom-csv-column-mapping-card=""
          className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
        >
          <CardContent className={COLUMN_MAPPING_FIELD_CARD_CONTENT_CLASS}>
            <Field className="gap-2" data-broker-csv-field="">
              <FieldLabel htmlFor="custom-csv-import-instrument" className={BROKER_ALERT_TITLE_CLASS}>
                Single instrument (whole file)
              </FieldLabel>
              <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                Required when your file has no symbol column.
              </FieldDescription>
              <ShadcnInput
                id="custom-csv-import-instrument"
                placeholder="e.g. XAUUSD"
                value={state.importInstrument}
                onChange={(e) =>
                  onStateChange({
                    importInstrument: e.target.value,
                    preview: null,
                  })
                }
                className="font-[family-name:var(--font-primary)]"
              />
            </Field>
          </CardContent>
        </Card>
      ) : null}

      {csvImport.import_modes && csvImport.import_modes.length > 1 ? (
        <Card
          data-custom-csv-column-mapping-card=""
          className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
        >
          <CardContent className={COLUMN_MAPPING_FIELD_CARD_CONTENT_CLASS}>
            <Field className="gap-2" data-broker-csv-field="">
              <FieldLabel htmlFor="custom-csv-import-mode" className={BROKER_ALERT_TITLE_CLASS}>
                Row layout
              </FieldLabel>
              <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                Set on the layout step. Start over from Choose layout to change it.
              </FieldDescription>
              <Select value={state.importMode} disabled>
                <SelectTrigger id="custom-csv-import-mode" className={BROKER_SELECT_TRIGGER_CLASS}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent {...COLUMN_MAPPING_SELECT_CONTENT_PROPS}>
                  {csvImport.import_modes.map((mode) => (
                    <SelectItem key={mode.id} value={mode.id}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default CustomCsvColumnMappingStep;
